/**
 * Names for the rungs of the in-game planet ladder, so a milestone can be *reported* and not only
 * drawn.
 *
 * The ladder itself is `src/game/ui/PlanetStack.ts`: one slot per star earned **this game** (a star
 * is `POINTS_PER_STAR` of score, see level.ts), Mercury first and the Sun ninth, after which the
 * run keeps going and the Sun changes state instead of a new body lighting up.
 *
 * The names are duplicated here rather than imported from `PlanetStack`, which pulls in Phaser —
 * this module is read by the analytics call site in RoundScene and by node-environment tests, and
 * neither should need a rendering library to answer "what is star 6 called". `milestones.test.ts`
 * is what keeps the two lists honest; it asserts the order matches `BODIES`.
 *
 * Only *named* levels are reported. Levels 10–12 deliberately have none: the Sun simply grows
 * brighter there, so there is nothing new to tell either the player or us.
 */

/** Slot order, innermost first — mirrors `BODIES` in src/game/ui/PlanetStack.ts. */
export const PLANETS = [
  'mercury', 'venus', 'earth', 'mars', 'jupiter', 'saturn', 'uranus', 'neptune', 'sun',
] as const;

/**
 * The top of the whole ladder: the star level at which the Sun's slot becomes a galaxy, and the
 * last thing the game has to show.
 *
 * Named rather than left as a bare `17` in the map below because a second reader now depends on
 * it: the Scoreboard's middle star is "reach the galaxy in one game" (specs/scoreboard.md §3.2).
 * One constant, so the rating and the drawing cannot come to disagree about where the galaxy is.
 */
export const GALAXY_STARS = 17;

/**
 * The star level at which the moon crosses the Sun — the ladder's one EVENT above the Sun, and the
 * rung the 🍝 Plate is earned on (specs/recipe-mode.md §2).
 *
 * Named for the same reason GALAXY_STARS is: a second reader now depends on it
 * (PASTA_AT_STACK_STARS in progression/config.ts), and a treat that lands on a rung the ladder does
 * not draw is a silent bug. `PlanetStack.ECLIPSE_AT` reads this too, so the drawing, the naming and
 * the prize cannot come to disagree about where the eclipse is.
 */
export const ECLIPSE_STARS = 14;

/**
 * The star level at which the Sun reddens — the first thing it does above the full stack that is a
 * change of STATE rather than more of the last one, and the rung the 🪱 Space Worm is earned on
 * (specs/space-worm-mode.md §2).
 *
 * Named for the same reason ECLIPSE_STARS and GALAXY_STARS are: a second reader depends on it now
 * (WORM_AT_STACK_STARS in progression/config.ts), and `PlanetStack.SUN_ORANGE_AT` draws it. Stars
 * 9 → 14 was the ladder's longest unpaid climb; this is what fills it.
 */
export const SUN_RED_STARS = 13;

/**
 * The star level at which the Sun turns violet, and the rung the 🚀 Rocket Read treat is earned on
 * (specs/rocket-read-mode.md §2). Named for the same reason SUN_RED_STARS is: a second reader
 * depends on it (ROCKET_AT_STACK_STARS in progression/config.ts), and `PlanetStack.SUN_VIOLET_AT`
 * draws it.
 */
export const SUN_VIOLET_STARS = 15;

/**
 * The Sun's late game, past the point where more halo says nothing new. Keys are star levels and
 * mirror `SUN_ORANGE_AT` / `ECLIPSE_AT` / `SUN_VIOLET_AT` / `SUPERNOVA_AT` / `GALAXY_AT` in
 * PlanetStack. The galaxy is the last thing the game has; nothing follows it.
 */
export const LATE_SUN: ReadonlyMap<number, string> = new Map([
  [SUN_RED_STARS, 'sun_red'],
  [ECLIPSE_STARS, 'eclipse'],
  [SUN_VIOLET_STARS, 'sun_violet'],
  [16, 'supernova'],
  [GALAXY_STARS, 'galaxy'],
]);

/**
 * Enough of each rung's LOOK to draw it outside Phaser — the Scoreboard prints the furthest body a
 * lesson has reached as a small disc next to its name (specs/scoreboard.md §2.1), and that panel is
 * plain HTML in the overlay layer.
 *
 * A second copy of PlanetStack's palette, for exactly the reason the names above are a copy: this
 * module must not pull in a rendering library. The nine bodies are checked against PlanetStack's
 * own `BODIES` source by `milestones.test.ts`; the late-Sun entries are the tint and remnant colours
 * the same file paints over the Sun, which have no single constant to compare against and are the
 * spirit of the thing rather than a pixel match — the disc is 24px, and the supernova is a
 * four-second animation of shocked gas.
 */
export interface MilestoneArt {
  /** The disc itself. */
  fill: string;
  /** Surface marking (land, bands, storm) — a smaller blob offset up-left. */
  detail?: string;
  /** Saturn only. */
  ring?: boolean;
  /** A soft glow behind the disc: the Sun's halo, and what is left of it afterwards. */
  halo?: string;
}

export const MILESTONE_ART: ReadonlyMap<string, MilestoneArt> = new Map([
  ['mercury', { fill: '#9c8e82' }],
  ['venus', { fill: '#e0b055', detail: '#f2d79a' }],
  ['earth', { fill: '#3f7fd0', detail: '#4fae5a' }],
  ['mars', { fill: '#c35733', detail: '#e8dcd2' }],
  ['jupiter', { fill: '#d2a271', detail: '#a9764c' }],
  ['saturn', { fill: '#e3c98a', ring: true }],
  ['uranus', { fill: '#8fd3dd' }],
  ['neptune', { fill: '#3f5cc4', detail: '#2a3f9e' }],
  ['sun', { fill: '#ffcc33', detail: '#fff3b0', halo: '#ffd23f' }],
  ['sun_red', { fill: '#ff6a12', detail: '#ffcc33', halo: '#ff6a12' }],
  // The moon in front of it is the whole point of this rung, so the "detail" is the dark disc.
  ['eclipse', { fill: '#ffcc33', detail: '#1b2027', halo: '#ffd23f' }],
  ['sun_violet', { fill: '#8b4dff', detail: '#d9b8ff', halo: '#8b4dff' }],
  ['supernova', { fill: '#bcd8ff', detail: '#8f2f1c', halo: '#6d2c78' }],
  // Not a disc at all — the Scoreboard draws it as a spiral, so `fill` is its ARMS (the young
  // blue stars PlanetStack paints outward), `detail` the bright core and `halo` the diffuse disc.
  ['galaxy', { fill: '#bcd8ff', detail: '#fff3cf', halo: '#6a4fb0' }],
]);

/**
 * What the player just reached at `stars` stars, or null if that level has no name of its own.
 *
 * `stars` is the count earned this game — 1 lights Mercury, 8 Neptune, 9 the Sun.
 */
export function milestoneAt(stars: number): string | null {
  if (!Number.isFinite(stars)) return null;
  const level = Math.floor(stars);
  if (level >= 1 && level <= PLANETS.length) return PLANETS[level - 1];
  return LATE_SUN.get(level) ?? null;
}

/**
 * The furthest *named* milestone at or below `stars` — what a finished game actually reached.
 *
 * Not the same as `milestoneAt`: a game that ends on 10 stars stopped somewhere the ladder has no
 * name for, and the true answer is still "the Sun". Returns null below the first star.
 */
export function milestoneReached(stars: number): string | null {
  if (!Number.isFinite(stars)) return null;
  for (let level = Math.floor(stars); level >= 1; level--) {
    const name = milestoneAt(level);
    if (name !== null) return name;
  }
  return null;
}
